// @ts-nocheck
/**
 * Ads API service
 * Handles all ad-related API calls
 */

import api from './api';

/**
 * Get ads with optional filters
 */
export async function getAds(params?: {
  category?: string;
  region?: string;
  search?: string;
  featured?: number;
  limit?: number;
  offset?: number;
}): Promise<{
  data: unknown[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}> {
  const queryParams: Record<string, string | number | boolean | undefined> = {};

  if (params?.category !== undefined) {
    queryParams.category = params.category;
  }
  if (params?.region !== undefined) {
    queryParams.region = params.region;
  }
  if (params?.search !== undefined) {
    queryParams.search = params.search;
  }
  if (params?.featured !== undefined) {
    queryParams.featured = params.featured;
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset;
  }

  const response = await api.get<{
    data: unknown[];
    pagination?: { total: number; limit: number; offset: number };
  }>('/api/ads', queryParams);

  if (response.success && response.data) {
    return {
      data: response.data,
      pagination: response.pagination || { total: 0, limit: 20, offset: 0 },
    };
  }

  return {
    data: [],
    pagination: { total: 0, limit: 20, offset: 0 },
  };
}

/**
 * Get featured ads
 */
export async function getFeaturedAds(
  limit: number = 6,
  featuredLevel?: number
): Promise<unknown[]> {
  const queryParams: Record<string, number | boolean> = { limit, featured: true };

  // Note: featured=true ensures we only get featured ads (featured > 0)
  // The backend handles featured as boolean and converts to "featured > 0" in SQL
  // The returned data has featured as number (0=none, 1=normal, 2=large)

  const response = await api.get<{ data: unknown[] }>('/api/ads', queryParams);

  if (response.success && response.data) {
    return response.data;
  }

  return [];
}

/**
 * Get ad by ID
 */
export async function getAdById(id: string): Promise<unknown | null> {
  const response = await api.get<unknown>(`/api/ads/${id}`);

  if (response.success && response.data) {
    return response.data;
  }

  return null;
}

/**
 * Advanced search ads
 */
export async function searchAds(params?: {
  category?: string;
  region?: string;
  district?: string;
  keyword?: string;
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}): Promise<{
  data: unknown[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
  facets?: Record<string, Record<string, number>>;
}> {
  const queryParams: Record<string, string | number | boolean | undefined> = {};

  if (params?.category !== undefined) {
    queryParams.category = params.category;
  }
  if (params?.region !== undefined) {
    queryParams.region = params.region;
  }
  if (params?.district !== undefined) {
    queryParams.district = params.district;
  }
  if (params?.keyword !== undefined) {
    queryParams.keyword = params.keyword;
  }
  if (params?.sortBy !== undefined) {
    queryParams.sortBy = params.sortBy;
  }
  if (params?.sortOrder !== undefined) {
    queryParams.sortOrder = params.sortOrder;
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset;
  }

  const response = await api.get<{
    data: unknown[];
    pagination?: { total: number; limit: number; offset: number };
    facets?: Record<string, Record<string, number>>;
  }>('/api/ads/search', queryParams);

  if (response.success && response.data) {
    return {
      data: response.data,
      pagination: response.pagination || { total: 0, limit: 20, offset: 0 },
      facets: response.facets,
    };
  }

  return {
    data: [],
    pagination: { total: 0, limit: 20, offset: 0 },
  };
}
